<script setup lang="ts">
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Button,
	Dialog,
	DialogContent,
	DialogTrigger,
	DialogHeader,
	DialogTitle,
} from '@laam/ui/base';

import { useForm } from 'vee-validate';

import HowToMeasureFigure from './HowToMeasure.vue';
import CustomShirtForm from './CustomShirtForm.vue';
import CustomTrouserForm from './CustomTrouserForm.vue';
import { customShirtFormSchema, type CustomFormTypes } from './types';
import { createReusableTemplate } from '@vueuse/core';

const emits = defineEmits<{
	(e: 'saveData', values: CustomFormTypes): void;
}>();

const isOpen = ref(false);

const form = useForm({
	validationSchema: customShirtFormSchema,
	initialValues: {
		unitsShirt: 'Cm',
		unitsTrouser: 'Cm',
	},
});

const onSubmit = form.handleSubmit(onSuccess, onInvalidSubmit);

async function onSuccess(values: CustomFormTypes) {
	console.log('Form submitted!', values);
	emits('saveData', values);
	isOpen.value = false;
}

function onInvalidSubmit({ values, errors, results }: any) {
	console.log('onInvalidSubmit values', values); // current form values
	console.log('onInvalidSubmit errors', errors); // a map of field names and their first error message
	console.log('onInvalidSubmit results', results); // a detailed map of field names and their validation results
}

const customShirt = {
	'Shirt Arm Hole': null,
	'Shirt Bottom': null,
	'Shirt Chest': null,
	'Shirt Hip': null,
	'Shirt Length': null,
	'Shirt Shoulder': null,
	'Shirt Sleeve Length': null,
	'Shirt Waist': null,
	'Shirt Wrist': null,
};

const customTrouser = {
	'Shirt Arm Hole': null,
	'Shirt Bottom': null,
	'Shirt Chest': null,
	'Shirt Hip': null,
	'Shirt Length': null,
	'Shirt Shoulder': null,
	'Shirt Sleeve Length': null,
	'Shirt Waist': null,
	'Shirt Wrist': null,
};

const [UseTemplate, CustomSizeChartForm] = createReusableTemplate();
</script>

<template class="pdp__size-chart">
	<div>
		<UseTemplate>
			<Tabs default-value="size-chart" class="flex flex-col">
				<TabsList
					class="py-none grid grid-cols-2 bg-white border-b border-gray-200 border-solid rounded-none"
				>
					<TabsTrigger
						value="size-chart"
						class="mx-xl size-chart-tab data-[state=active]:text-gray-700 data-[state=active]:border-gray-700 border-solid text-gray-500 pb-xl rounded-none border-0 shadow-transparent border-b-2 px-xs p-xs border-white"
					>
						Size Chart
					</TabsTrigger>
					<TabsTrigger
						value="guide"
						class="size-chart-tab mx-xl data-[state=active]:text-gray-700 data-[state=active]:border-gray-700 border-solid text-gray-500 pb-xl rounded-none border-0 shadow-transparent border-b-2 px-xs p-xs border-white"
					>
						Size Guide
					</TabsTrigger>
				</TabsList>
				<TabsContent value="size-chart" class="overflow-auto h-[60vh]">
					<div class="gap-5xl px-3xl py-5xl flex flex-col">
						<form id="customShirtForm" @submit.prevent="onSubmit">
							<div class="pb-12xl">
								<CustomShirtForm :custom-shirt="customShirt" />
								<div class="my-3xl border-b border-gray-300" />
								<CustomTrouserForm :custom-trouser="customTrouser" />
							</div>
							<div
								class="bottom-none left-none right-none py-3xl px-7xl h-12xl gap-sm fixed z-10 flex flex-row justify-center bg-white border-t border-gray-200"
							>
								<Button
									variant="secondary"
									class="w-full"
									@click="form.resetForm()"
								>
									<span class="text-md font-medium text-gray-800"> Reset </span>
								</Button>
								<Button variant="primary" class="w-full" type="submit">
									<span class="text-md font-medium text-white">Save</span>
								</Button>
							</div>
						</form>
					</div>
				</TabsContent>
				<TabsContent value="guide" class="h-[60vh] overflow-auto">
					<div class="px-3xl py-8xl">
						<HowToMeasureFigure class="pt-lg w-full" />
					</div>
				</TabsContent>
			</Tabs>
		</UseTemplate>

		<Drawer
			v-if="isMobileView()"
			v-model:open="isOpen"
			class=""
			@update:open="isOpen != isOpen"
		>
			<DrawerTrigger>
				<span> Custom Size </span>
			</DrawerTrigger>
			<DrawerContent class="-mx-3xl min-h-[80vh]">
				<DrawerHeader>
					<DrawerTitle class="text-lg font-semibold text-gray-800">
						Size Chart
					</DrawerTitle>

					<CustomSizeChartForm />
				</DrawerHeader>
			</DrawerContent>
		</Drawer>

		<Dialog v-else v-model:open="isOpen" @update:open="isOpen != isOpen">
			<DialogTrigger> Custom Size </DialogTrigger>
			<DialogContent class="product_size_chart-modal rounded-xl w-[50%] h-fit">
				<DialogHeader class="mb-5xl grow-0">
					<DialogTitle class="mb-xs text-lg font-semibold">
						Size Guide
					</DialogTitle>
				</DialogHeader>

				<CustomSizeChartForm />
			</DialogContent>
		</Dialog>
	</div>
</template>
